import { ArrowDownRight, ArrowUpRight, Activity } from 'lucide-preact'
import type { ComponentChildren } from 'preact'
import { memo } from 'preact/compat'
import type { DashboardWindowCountDto } from '@/types/dashboard'

interface DashboardStatTileProps {
  /** Rendered one word per line, matching the existing tile headings. */
  titleLines: string[]
  icon: ComponentChildren
  count: DashboardWindowCountDto | number | null | undefined
  /** Static pill shown when there is no comparison to draw, e.g. "Active". */
  badge?: string
  badgeTitle?: string
  /**
   * The window this number covers, e.g. "All Time" or "Last 30 Days". Rendered as a caption so the
   * figure can be read — or quoted into a deck — without hunting for the range control, and so the
   * absence of a delta on All Time reads as "no prior period" rather than as a pill that failed.
   */
  rangeLabel?: string
  loading?: boolean
  error?: string | null
  /** The server could not compute this tile. Shown as broken rather than as a zero. */
  failed?: boolean
}

/**
 * The shared shell for the dashboard's single-number tiles.
 *
 * The four count tiles were near-identical files that differed only in heading, icon and which
 * fetch they ran. Now that the data arrives in one bundle, the only real differences are the
 * heading and the icon.
 */
export const DashboardStatTile = memo(
  ({ titleLines, icon, count, badge, badgeTitle, rangeLabel, loading = false, error = null, failed = false }: DashboardStatTileProps) => {
    if (loading) {
      return (
        <div className="tile">
          <div className="flex items-start justify-between mb-4">
            <div className="flex flex-col gap-1">
              <div className="h-3 w-24 rounded-md bg-shimmer-light animate-shimmer" />
              <div className="h-3 w-20 rounded-md bg-shimmer-light animate-shimmer" />
            </div>
            <span className="w-5 h-5 rounded-md bg-shimmer-light animate-shimmer" />
          </div>
          <div className="flex items-end gap-4 mt-auto">
            <div className="flex-1">
              <div className="h-8 w-20 mb-2 rounded-md bg-shimmer-light animate-shimmer" />
              <div className="h-3 w-24 rounded-md bg-shimmer-light animate-shimmer" />
            </div>
            <div className="h-5 w-12 rounded-md bg-shimmer-light animate-shimmer" />
          </div>
        </div>
      )
    }

    const value = typeof count === 'number' ? count : count?.Current ?? null
    const previous = typeof count === 'number' ? null : count?.Previous ?? null

    // Only drawn when there is a preceding window. All Time has none, so no delta is shown at all
    // rather than a zero that would read as "no change".
    const delta = value !== null && previous !== null ? value - previous : null
    const direction = delta === null ? null : delta > 0 ? 'up' : delta < 0 ? 'down' : 'flat'
    const percent = delta !== null && previous ? ((delta / previous) * 100).toFixed(1) : null

    return (
      <div className="tile">
        <div className="flex items-start justify-between mb-3">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 leading-tight">
            {titleLines.map(line => (
              <span key={line} className="block">
                {line}
              </span>
            ))}
          </h3>
          {icon}
        </div>

        {error && <div className="text-sm text-red-600 dark:text-red-400">{error}</div>}

        {!error && failed && (
          <div className="text-sm text-red-600 dark:text-red-400">Couldn't load this metric</div>
        )}

        {!error && !failed && value !== null && (
          <div className="flex items-end justify-between gap-4">
            <div className="text-4xl font-bold text-green-600 dark:text-green-400 leading-none">
              {value.toLocaleString()}
            </div>
            <div className="flex flex-col items-end gap-1">
              {direction && (
                <span
                  className={`pill ${direction === 'up' ? 'pill-up' : direction === 'down' ? 'pill-down' : 'pill-flat'} text-xs2`}
                  title="Difference vs previous window"
                >
                  {direction === 'up' && <ArrowUpRight className="w-3.5 h-3.5" />}
                  {direction === 'down' && <ArrowDownRight className="w-3.5 h-3.5" />}
                  {direction === 'flat' && <Activity className="w-3.5 h-3.5" />}
                  <span>{Math.abs(delta!).toLocaleString()}</span>
                  {percent !== null && direction !== 'flat' && <span className="opacity-70">({percent}%)</span>}
                </span>
              )}
              {!direction && badge && (
                <span className="pill pill-flat text-xs2" title={badgeTitle}>
                  {badge}
                </span>
              )}
            </div>
          </div>
        )}

        {!error && !failed && value === null && (
          <div className="text-sm text-slate-500 dark:text-slate-400">No data</div>
        )}

        {!error && !failed && value !== null && rangeLabel && (
          <div className="mt-3 text-2xs tracking-wide uppercase text-slate-500 dark:text-slate-400">
            {previous === null ? rangeLabel : `vs previous ${rangeLabel.replace(/^Last /i, '')}`}
          </div>
        )}
      </div>
    )
  },
)
